/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import * as auth0 from 'auth0-js';
import Auth0Error = auth0.Auth0Error;
import jwtDecode from 'jwt-decode';
import { environment } from 'environments/environment';
import { Observable, Observer } from 'rxjs';
import { AuthService } from './auth.service';
import { HttpHeaders } from '@angular/common/http';
import { UserDetails, UserDetailsFactory } from 'app/user-auth/models';
import AuthOptions = auth0.AuthOptions;
import { observableOf, observableThrow } from 'app/shared/rxjs/rxjs.utils';
import { TenantDetails } from 'app/page-modules/tenant-management/models';

/** The type of the decoded JWT. */
interface JwtToken {
  azp: string;
  sub: string;
}

/** Auth0 implementation of Auth service. */
@Injectable()
export class Auth0AuthService extends AuthService {
  readonly webauthOptions: Partial<AuthOptions> = {
    audience: environment.audience,
    domain: environment.auth0Url,
    clientID: ''
  };

  getRequestHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_client_token');
    if (!token) {
      throw new Error('Token not found');
    }

    return new HttpHeaders().set('Authorization', 'Bearer ' + token);
  }

  setAccessToken(token: string): void {
    localStorage.setItem('auth_client_token', token);
  }

  unauthenticate(): void {
    const returnTo = `${environment.loginUrl}?redirectUrl=${window.location.origin}`;
    try {
      const clientId = localStorage.getItem('auth_client_id');

      localStorage.removeItem('auth_client_sub');
      localStorage.removeItem('auth_client_id_token');
      localStorage.removeItem('auth_client_token');
      localStorage.removeItem('auth_client_id');

      if (clientId) {
        const webAuth = new auth0.WebAuth({
          audience: environment.audience,
          domain: environment.auth0Url,
          clientID: clientId
        } as AuthOptions);

        webAuth.logout({ returnTo });
      }
    } finally {
      window.location.href = returnTo;
    }
  }

  authenticate(): Observable<UserDetails> {
    return new Observable((observer: Observer<UserDetails>) => {
      // The client ID is required for establishing a connection with Auth0
      let clientId;
      const urlParams = this.parseQueryString(window.location.hash.substr(1));
      const token = urlParams.access_token;
      const idToken = urlParams.id_token;

      // When logging in from the auth site, the access token is passed in via a hash fragment
      if (token) {
        try {
          const decodedToken = jwtDecode<JwtToken>(token);
          // Client ID is equivalent to the JWT authorized party
          clientId = decodedToken.azp;
        } catch (e) {
          // broken token came from okta
          const decodedToken = jwtDecode<{ clientID: string }>(idToken);
          clientId = decodedToken.clientID;
        }
      } else {
        // Check whether the user has a saved session
        clientId = localStorage.getItem('auth_client_id');
      }
      if (clientId) {
        localStorage.setItem('auth_client_id', clientId);
        this.renewAuthentication(clientId, observer);
      } else {
        // The user is not logging in and does not have a previous session saved
        observer.error('Could not authenticate user.');
        observer.complete();
        return undefined;
      }
    });
  }

  canLoginToTenant(userDetails: UserDetails, tenantDetails: TenantDetails): boolean {
    // MUI users can currently log into all enabled tenants.
    return true;
  }

  requiresLoginReason(tenantDetails: TenantDetails): boolean {
    return tenantDetails.tags.map(tag => tag.toLowerCase()).includes('prod');
  }

  loginToTenant(
    tenantClientId: string,
    orgId?: string,
    previewEnvironment?: string
  ): Observable<void> {
    const clientId = localStorage.getItem('auth_client_id');
    if (clientId) {
      const webAuth = new auth0.WebAuth({
        audience: environment.audience,
        domain: environment.auth0Url,
        clientID: tenantClientId
      } as AuthOptions);

      let redirectUri;
      if (orgId && previewEnvironment) {
        redirectUri = `https://${orgId}-${previewEnvironment}.${environment.previewUrl}`;
      } else {
        redirectUri = environment.analyticsUrl;
      }

      webAuth.authorize({
        connection: environment.oktaConnection,
        clientID: tenantClientId,
        redirectUri: redirectUri,
        audience: environment.audience,
        scope: 'openid profile email name',
        responseType: 'token'
      });
    } else {
      return observableThrow('No clientId available in localStorage');
    }
    return observableOf();
  }

  private renewAuthentication(clientId: string, observer: Observer<UserDetails>): void {
    const webauthOptions = { ...this.webauthOptions, clientID: clientId };
    const webauth = new auth0.WebAuth(webauthOptions as AuthOptions);
    const redirectUri = `${window.location.origin}/callback.html`;
    const renewAuthOptions = {
      scope: 'openid profile email user_id',
      responseType: 'token id_token',
      redirectUri: redirectUri,
      usePostMessage: true,
      // This binds the Window.postMessage callback to only the auth0 message, ignoring webpack messages
      // see https://github.com/auth0/auth0.js/commit/ecc17612c4470a60a8e8fbdf227c1d6811cf2b86
      postMessageDataType: 'auth0:silent-authentication',
      nonce: this.randomString(16)
    };

    webauth.renewAuth(renewAuthOptions, (renewAuthErr: Auth0Error, auth) => {
      if (renewAuthErr) {
        observer.error(renewAuthErr);
        observer.complete();
        return;
      }
      const accessToken = auth.accessToken;
      const idToken = auth.idToken;
      const jwtToken = jwtDecode<JwtToken>(accessToken);
      // eslint-disable-next-line
      const userRoles = (<any>jwtToken)[`${environment.audience}/user_roles`] || [];

      this.setAccessToken(accessToken);
      localStorage.setItem('auth_client_id_token', idToken);
      localStorage.setItem('auth_client_sub', jwtToken.sub);

      this.fetchUserDetails(webauthOptions, accessToken, userRoles, observer);
    });
  }

  private fetchUserDetails(
    webAuthOptions: Partial<AuthOptions>,
    accessToken: string,
    userRoles: string[],
    observer: Observer<UserDetails>
  ): void {
    const webauth = new auth0.WebAuth(webAuthOptions as AuthOptions);
    webauth.client.userInfo(accessToken, (userInfoErr, user) => {
      if (userInfoErr) {
        observer.error(userInfoErr);
        observer.complete();
        return;
      }

      observer.next(UserDetailsFactory.userDetails(user, userRoles));
      observer.complete();
    });
  }

  private randomString(length: number): string {
    const bytes = new Uint8Array(length);
    const random = window.crypto.getRandomValues(bytes);
    const result: string[] = [];
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~';

    random.forEach(c => result.push(charset[c % charset.length]));

    return result.join('');
  }

  private parseQueryString(query: string): { [index: string]: string } {
    const vars = query.split('&');
    const queryString: { [index: string]: string } = {};
    for (const param of vars) {
      const pair = param.split('=');
      queryString[pair[0]] = decodeURIComponent(pair[1]);
    }
    return queryString;
  }
}
